import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { evaluateBillingContext } from '../../utils/orderInvoiceRules';

/* ─── Types ─────────────────────────────────────────── */
interface InvoiceItem {
  _key: string;
  productId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  vatRate: number;
  saleType: 'unit' | 'box';
  box_quantity?: number;
}

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  companyName: string;
  companyNumber: string;
  address: string;
  city: string;
  postcode: string;
  country: string;
  credit_terms: number;
  credit_balance: number;
}

interface Product {
  id: string;
  name: string;
  price: number;
  sku: string;
  wholesale_price?: number;
  box_quantity?: number;
}

/* ─── Helpers ────────────────────────────────────────── */
const newItem = (): InvoiceItem => ({
  _key: `k-${Date.now()}-${Math.random()}`,
  productId: '',
  description: '',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  vatRate: 20,
  saleType: 'unit',
});

const calcDueDate = (dateStr: string, days: number): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const toNum = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/* ─── Constants ──────────────────────────────────────── */
const PAYMENT_TERMS: Record<string, number> = {
  'Due on Receipt': 0, 'Net 7': 7, 'Net 14': 14, 'Net 30': 30, 'Net 60': 60,
};

const STATUS_COLORS: Record<string, string> = {
  Draft: '#6b7280', Sent: '#3b82f6',
  Paid: '#10b981', Overdue: '#ef4444', Cancelled: '#9ca3af',
};

/* ════════════════════════════════════════════════════════ */
const InvoiceEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  /* customer */
  const [isGuest, setIsGuest] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [fetchingCustomer, setFetchingCustomer] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditTermsDays, setCreditTermsDays] = useState(30);
  const [appliedCredit, setAppliedCredit] = useState(0);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  /* invoice meta */
  const [invoiceStatus, setInvoiceStatus] = useState('Draft');
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [taxMode, setTaxMode] = useState<'exclusive' | 'inclusive'>('exclusive');
  const [editReason, setEditReason] = useState('');

  const [form, setForm] = useState({
    invoiceNumber: '', invoiceDate: '', dueDate: '',
    customerAddress: '', email: '', phone: '',
    companyName: '', companyNumber: '', description: '', notes: '',
  });

  const [items, setItems] = useState<InvoiceItem[]>([newItem()]);

  const [invoiceLocked, setInvoiceLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState('');

  const isLocked = invoiceLocked;
  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* totals */
  const calculateTotals = useCallback(() => {
    let subtotal = 0, discountTotal = 0, vatAmount = 0;
    items.forEach(item => {
      const gross = (item.quantity || 0) * (item.unitPrice || 0);
      const discAmt = gross * ((item.discount || 0) / 100);
      const net = gross - discAmt;
      subtotal += gross;
      discountTotal += discAmt;
      if (taxMode === 'exclusive') vatAmount += (net * (item.vatRate || 0)) / 100;
      else vatAmount += net - net / (1 + (item.vatRate || 0) / 100);
    });
    const netAfterDiscount = subtotal - discountTotal;
    const total = taxMode === 'exclusive'
      ? netAfterDiscount + vatAmount - appliedCredit
      : netAfterDiscount - appliedCredit;
    return { subtotal, discountTotal, vatAmount, total: Math.max(0, total) };
  }, [items, taxMode, appliedCredit]);

  const totals = calculateTotals();

  /* load */
  useEffect(() => {
    if (!token || !id) return;
    const g = (inv: any, snake: string, camel?: string, fallback?: any) => {
      const v = inv?.[snake] ?? (camel ? inv?.[camel] : undefined);
      return v ?? fallback;
    };
    Promise.all([
      apiFetch(`${API_URL}/invoices/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
      apiFetch(`${API_URL}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
      apiFetch(`${API_URL}/products`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(async ([invRes, custRes, prodRes]) => {
        if (!invRes.ok) throw new Error('Invoice not found');
        const [invoice, custData, prodData] = await Promise.all([invRes.json(), custRes.json(), prodRes.json()]);

        const prodArr = Array.isArray(prodData) ? prodData : prodData?.products || [];
        const normalizedProducts: Product[] = prodArr.map((p: any) => ({
          id: String(p._id || p.id), name: p.name, price: toNum(p.price), sku: p.sku || '',
          wholesale_price: p.wholesale_price != null ? toNum(p.wholesale_price) : undefined,
          box_quantity: p.box_quantity != null ? toNum(p.box_quantity) : undefined,
        }));
        setProducts(normalizedProducts);

        const normalizedCustomers: Customer[] = (Array.isArray(custData) ? custData : []).map((c: any) => ({
          id: String(c._id || c.id), name: c.name, email: c.email || '',
          phone: c.phone || '', companyName: c.companyName || c.companyname || '',
          companyNumber: c.companyNumber || c.companynumber || '',
          address: c.address || '', city: c.city || '', postcode: c.postcode || '', country: c.country || '',
          credit_terms: c.credit_terms || 30, credit_balance: toNum(c.credit_balance),
        }));
        setCustomers(normalizedCustomers);

        const rawItems = Array.isArray(g(invoice, 'items')) ? g(invoice, 'items') : [];
        const loadedItems: InvoiceItem[] = rawItems.length > 0
          ? rawItems.map((it: any, idx: number) => ({
              _key: `loaded-${idx}-${Math.random()}`,
              productId: String(it.productId || it.product_id || ''),
              description: it.description || '',
              quantity: toNum(it.quantity, 1),
              unitPrice: toNum(it.unitPrice ?? it.unit_price, 0),
              discount: toNum(it.discount ?? it.discount_rate, 0),
              vatRate: toNum(it.vatRate ?? it.vat_rate, 20),
              saleType: (it.sale_type || it.saleType || 'unit') as 'unit' | 'box',
              box_quantity: it.box_quantity != null ? toNum(it.box_quantity) : undefined,
            }))
          : [{ _key: 'loaded-0', productId: '', description: g(invoice, 'description', 'description', ''), quantity: toNum(g(invoice, 'quantity', 'quantity', 1), 1), unitPrice: toNum(g(invoice, 'unit_price', 'unitPrice', 0), 0), discount: 0, vatRate: 20, saleType: 'unit' as const }];
        setItems(loadedItems);

        const customerId = String(g(invoice, 'customer_id', 'customerId', '') || '');
        const linked = normalizedCustomers.find(c => String(c.id) === customerId);
        const guestMode = !customerId || !linked;
        setIsGuest(guestMode);
        setSelectedCustomer(linked || null);
        if (linked) { setSearchTerm(linked.name); setCreditBalance(linked.credit_balance); setCreditTermsDays(linked.credit_terms || 30); }
        setAppliedCredit(toNum(g(invoice, 'applied_credit', 'appliedCredit', 0)));

        const loadedStatus = g(invoice, 'status', 'status', 'Draft');
        setInvoiceStatus(loadedStatus);
        const billing = evaluateBillingContext(invoice);
        setInvoiceLocked(!billing.canEditInvoiceLines);
        setLockMessage(billing.invoiceEditHint);
        if (!billing.canEditInvoiceLines) {
          showToast(`🔒 ${billing.invoiceEditHint}`, 'warning');
        }
        const invDate = g(invoice, 'invoice_date', 'invoiceDate', '');
        const dueDate = g(invoice, 'due_date', 'dueDate') || g(invoice, 'payment_due_date', 'paymentDueDate', '');
        const payTermsRaw = g(invoice, 'payment_terms', 'paymentTerms', 'Net 30');
        if (payTermsRaw && PAYMENT_TERMS[payTermsRaw] !== undefined) setPaymentTerms(payTermsRaw);

        const clientName = g(invoice, 'client_name', 'clientName', '');
        if (guestMode) setSearchTerm(clientName);

        setForm({
          invoiceNumber: g(invoice, 'invoice_number', 'invoiceNumber', ''),
          invoiceDate: invDate ? invDate.split('T')[0] : '',
          dueDate: dueDate ? dueDate.split('T')[0] : '',
          customerAddress: g(invoice, 'customer_address', 'customerAddress', linked?.address || ''),
          email: g(invoice, 'customer_email', 'customerEmail', linked?.email || ''),
          phone: g(invoice, 'customer_phone', 'customerPhone', linked?.phone || ''),
          companyName: g(invoice, 'company_name', 'companyName', linked?.companyName || ''),
          companyNumber: g(invoice, 'company_number', 'companyNumber', linked?.companyNumber || ''),
          description: g(invoice, 'description', 'description', ''),
          notes: g(invoice, 'notes', 'notes', ''),
        });
      })
      .catch(err => { console.error('Error loading invoice:', err); showToast('Failed to load invoice', 'error'); navigate('/invoices/list'); })
      .finally(() => setPageLoading(false));
  }, [id, token, navigate]);

  const handleCustomerSelect = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(customer.name);
    setFetchingCustomer(true);
    try {
      const res = await apiFetch(`${API_URL}/customers/${customer.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const c = await res.json();
        const terms = c.credit_terms || 30;
        const balance = toNum(c.credit_balance);
        const addr = [c.address, c.city, c.postcode, c.country].filter(Boolean).join(', ');
        setCreditTermsDays(terms); setCreditBalance(balance); setAppliedCredit(0);
        setForm(f => ({ ...f, customerAddress: addr, email: c.email || '', phone: c.phone || '', companyName: c.companyName || c.companyname || '', companyNumber: c.companyNumber || c.companynumber || '', dueDate: calcDueDate(f.invoiceDate || new Date().toISOString().split('T')[0], terms) }));
        setSelectedCustomer({ ...customer, credit_terms: terms, credit_balance: balance });
      }
    } catch { setForm(f => ({ ...f, customerAddress: customer.address || '' })); }
    finally { setFetchingCustomer(false); }
  };

  const handleProductSelect = (key: string, productId: string) => {
    const product = products.find(p => String(p.id) === String(productId));
    if (!product) return;
    setItems(prev => prev.map(it =>
      it._key === key ? { ...it, productId: String(product.id), description: product.name, unitPrice: product.price, saleType: 'unit', box_quantity: product.box_quantity } : it
    ));
  };

  const handleSaleTypeChange = (key: string, saleType: 'unit' | 'box') => {
    setItems(prev => prev.map(it => {
      if (it._key !== key) return it;
      const product = products.find(p => String(p.id) === it.productId);
      if (!product) return { ...it, saleType };
      if (saleType === 'box') {
        const bq = product.box_quantity || 0;
        if (!bq) return it;
        const boxPrice = product.wholesale_price != null && product.wholesale_price > 0
          ? product.wholesale_price * bq
          : product.price * bq;
        return { ...it, saleType, unitPrice: boxPrice, box_quantity: bq };
      } else {
        return { ...it, saleType, unitPrice: product.price, box_quantity: product.box_quantity };
      }
    }));
  };

  const handleItemChange = (key: string, field: keyof Omit<InvoiceItem, '_key'>, value: any) => {
    setItems(prev => prev.map(it => it._key === key ? { ...it, [field]: value } : it));
  };

  const addItem = () => setItems(prev => [...prev, newItem()]);
  const removeItem = (key: string) => { if (items.length > 1) setItems(prev => prev.filter(it => it._key !== key)); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const customerName = isGuest ? searchTerm : (selectedCustomer?.name || '');
    if (!customerName) { showToast('Please select or enter a customer name'); return; }
    if (!editReason.trim()) { showToast('Please enter a reason for modification (required for audit log)'); return; }
    const filteredItems = items.filter(it => it.description && it.quantity > 0);
    if (filteredItems.length === 0) { showToast('Please add at least one valid line item'); return; }

    const payload = {
      customerId: isGuest ? null : selectedCustomer?.id, customer_id: isGuest ? null : selectedCustomer?.id,
      clientName: customerName, client_name: customerName,
      customerEmail: form.email, customer_email: form.email,
      customerPhone: form.phone, customer_phone: form.phone,
      companyName: form.companyName, company_name: form.companyName,
      companyNumber: form.companyNumber, company_number: form.companyNumber,
      customerAddress: form.customerAddress, customer_address: form.customerAddress,
      invoiceNumber: form.invoiceNumber, invoice_number: form.invoiceNumber,
      invoiceDate: form.invoiceDate, invoice_date: form.invoiceDate,
      dueDate: form.dueDate, due_date: form.dueDate, paymentDueDate: form.dueDate, payment_due_date: form.dueDate,
      paymentTerms, payment_terms: paymentTerms,
      status: invoiceStatus, description: form.description, notes: form.notes,
      editReason, editTimestamp: new Date().toISOString(),
      appliedCredit, applied_credit: appliedCredit,
      items: filteredItems.map(it => ({
        productId: it.productId || null, product_id: it.productId || null,
        description: it.description, quantity: it.quantity,
        unitPrice: it.unitPrice, unit_price: it.unitPrice,
        discount: it.discount || 0, vatRate: it.vatRate, vat_rate: it.vatRate,
        amount: (it.quantity * it.unitPrice) * (1 - (it.discount || 0) / 100),
      })),
      subtotal: totals.subtotal, discountTotal: totals.discountTotal,
      total_vat: totals.vatAmount, vatAmount: totals.vatAmount,
      amount: totals.total, total: totals.total,
    };

    try {
      setSaving(true);
      const res = await apiFetch(`${API_URL}/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) { showToast('✅ Invoice updated successfully!'); navigate(`/invoices/view/${id}`); }
      else { const err = await res.json().catch(() => ({})); showToast(`Failed: ${err.message || 'Unknown error'}`, 'error'); }
    } catch { showToast('Network error — please try again', 'error'); }
    finally { setSaving(false); }
  };

  if (pageLoading) return <div style={{ padding: 60, textAlign: 'center', fontSize: 18 }}>⏳ Loading Invoice...</div>;

  if (isLocked) {
    return (
      <div style={{ padding: '40px 24px', maxWidth: 700, margin: '40px auto' }}>
        <div style={{ background: '#fef3c7', border: '2px solid #fbbf24', borderRadius: 16, padding: '32px 36px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🔒</div>
          <h2 style={{ color: '#92400e', margin: '0 0 10px', fontSize: 22 }}>Invoice locked — editing disabled</h2>
          <p style={{ color: '#78350f', fontSize: 15, marginBottom: 24, lineHeight: 1.6 }}>
            {lockMessage || 'This invoice cannot be edited. Issue a Credit Note to adjust amounts after payment or credit.'}
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => navigate(`/invoices/view/${id}`)}
              style={{ padding: '12px 28px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
              👁 View Invoice
            </button>
            <button type="button" onClick={() => navigate('/invoices/list')}
              style={{ padding: '12px 28px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
              ← Back to List
            </button>
          </div>
          <div style={{ marginTop: 24, padding: '12px 20px', background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 13, color: '#6b7280' }}>
            <strong>Current status:</strong>&nbsp;
            <span style={{ padding: '3px 12px', borderRadius: 20, background: STATUS_COLORS[invoiceStatus] + '22', color: STATUS_COLORS[invoiceStatus], fontWeight: 700, border: `1px solid ${STATUS_COLORS[invoiceStatus]}` }}>
              {invoiceStatus}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media (max-width: 640px) {
          .ie-hrow { flex-direction: column !important; }
          .ie-grid2 { grid-template-columns: 1fr !important; }
          .ie-submit-row { flex-direction: column; }
          .ie-submit-row button { width: 100% !important; }
        }
      `}</style>
      <form onSubmit={handleSubmit} style={st.form}>

        {/* Header */}
        <div style={st.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <h2 style={st.title}>✏️ Edit Invoice</h2>
            <span style={{ padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: STATUS_COLORS[invoiceStatus] + '22', color: STATUS_COLORS[invoiceStatus], border: `1.5px solid ${STATUS_COLORS[invoiceStatus]}` }}>{invoiceStatus}</span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={st.invoiceNumber}>{form.invoiceNumber}</div>
            <button type="button" onClick={() => navigate(`/invoices/view/${id}`)} style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>👁 View</button>
            <button type="button" onClick={() => navigate('/invoices/list')} style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>← Back</button>
          </div>
        </div>

        {/* Customer Section */}
        <div style={st.section}>
          <h3 style={st.sectionTitle}>Customer Information</h3>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <button type="button" onClick={() => setIsGuest(false)} style={{ ...st.toggleBtn, ...(isGuest ? st.toggleInactive : st.toggleActive) }}>📋 Existing Customer</button>
            <button type="button" onClick={() => setIsGuest(true)} style={{ ...st.toggleBtn, ...(!isGuest ? st.toggleInactive : st.toggleActive) }}>👤 Guest Customer</button>
          </div>

          {!isGuest ? (
            <div style={{ position: 'relative' }}>
              <label style={st.label}>Search Customer</label>
              <input type="text" value={searchTerm} placeholder="Start typing customer name..."
                onChange={e => { setSearchTerm(e.target.value); setSelectedCustomer(null); }} style={st.input} />
              {searchTerm && !selectedCustomer && filteredCustomers.length > 0 && (
                <div style={st.dropdown}>
                  {filteredCustomers.slice(0, 8).map(c => (
                    <div key={c.id} style={st.dropdownItem} onClick={() => handleCustomerSelect(c)}>
                      <strong>{c.name}</strong>
                      {c.companyName && <div style={{ fontSize: 12, color: '#6b7280' }}>{c.companyName}</div>}
                      {c.email && <div style={{ fontSize: 12, color: '#9ca3af' }}>{c.email}</div>}
                    </div>
                  ))}
                </div>
              )}
              {fetchingCustomer && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>⏳ Loading customer...</div>}
              {selectedCustomer && !fetchingCustomer && (
                <div style={st.selectedBadge}>
                  ✓ {selectedCustomer.name}{selectedCustomer.companyName ? ` — ${selectedCustomer.companyName}` : ''}
                  <button type="button" onClick={() => { setSelectedCustomer(null); setSearchTerm(''); }}
                    style={{ marginLeft: 10, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>✕ Change</button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label style={st.label}>Guest Customer Name *</label>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Enter customer name" style={st.input} required />
            </div>
          )}

          {!isGuest && creditBalance > 0 && (
            <div style={st.creditBanner}>
              <span>💰 <strong>£{creditBalance.toFixed(2)}</strong> credit available —</span>
              <button type="button" style={st.creditApplyBtn} onClick={() => setShowCreditModal(true)}>Apply?</button>
            </div>
          )}
          {appliedCredit > 0 && (
            <div style={st.creditApplied}>
              ✅ £{appliedCredit.toFixed(2)} credit will be applied
              <button type="button" style={{ marginLeft: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={() => setAppliedCredit(0)}>Remove</button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }} className="ie-grid2">
            <div><label style={st.label}>Email</label><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={st.input} placeholder="billing@client.com" /></div>
            <div><label style={st.label}>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={st.input} placeholder="+44..." /></div>
            <div><label style={st.label}>Company Name</label><input value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} style={st.input} placeholder="Company Ltd" /></div>
            <div><label style={st.label}>Company Number</label><input value={form.companyNumber} onChange={e => setForm(f => ({ ...f, companyNumber: e.target.value }))} style={st.input} placeholder="12345678" /></div>
          </div>

          <label style={st.label}>Customer Address</label>
          <textarea value={form.customerAddress} onChange={e => setForm(f => ({ ...f, customerAddress: e.target.value }))} style={{ ...st.input, height: 60, resize: 'vertical' as const }} />
        </div>

        {/* Invoice Details Row */}
        <div className="ie-hrow" style={{ display: 'flex', gap: 15 }}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Invoice Number *</label>
            <input value={form.invoiceNumber} onChange={e => setForm(f => ({ ...f, invoiceNumber: e.target.value }))} style={st.input} required />
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Invoice Date *</label>
            <input type="date" value={form.invoiceDate}
              onChange={e => { const d = e.target.value; setForm(f => ({ ...f, invoiceDate: d, dueDate: calcDueDate(d, PAYMENT_TERMS[paymentTerms] ?? 30) })); }}
              style={st.input} required />
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Payment Terms</label>
            <select value={paymentTerms} onChange={e => { const t = e.target.value; setPaymentTerms(t); setForm(f => ({ ...f, dueDate: calcDueDate(f.invoiceDate, PAYMENT_TERMS[t] ?? 30) })); }} style={{ ...st.input, marginBottom: 6 }}>
              {Object.keys(PAYMENT_TERMS).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Due Date *</label>
            <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} style={st.input} required />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: -10, marginBottom: 10 }}>{paymentTerms} applied</div>
          </div>
        </div>

        <label style={st.label}>Description *</label>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          style={{ ...st.input, height: 80, resize: 'vertical' as const }} placeholder="Description of goods or services provided..." required />

        {/* Items Table */}
        <h3 style={{ marginTop: 30, marginBottom: 15, fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Invoice Items</h3>
        <div style={{ overflowX: 'auto', marginBottom: 20, borderRadius: 12, border: '1px solid #e5e7eb' }}>
          <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={st.th}>Product</th>
                <th style={st.th}>Description</th>
                <th style={{ ...st.th, textAlign: 'center' as const }}>Qty</th>
                <th style={{ ...st.th, textAlign: 'right' as const }}>Unit Price (£)</th>
                <th style={{ ...st.th, textAlign: 'center' as const }}>Disc %</th>
                <th style={{ ...st.th, textAlign: 'center' as const }}>VAT %</th>
                <th style={{ ...st.th, textAlign: 'right' as const }}>Line Total</th>
                <th style={{ ...st.th, textAlign: 'center' as const }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const gross = (item.quantity || 0) * (item.unitPrice || 0);
                const discAmt = gross * ((item.discount || 0) / 100);
                const lineTotal = gross - discAmt;
                return (
                  <tr key={item._key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={st.td}>
                      <select value={item.productId || ''} onChange={e => handleProductSelect(item._key, e.target.value)} style={st.select}>
                        <option value="">Select product...</option>
                        {products.map(p => <option key={p.id} value={String(p.id)}>{p.name} (£{p.price.toFixed(2)})</option>)}
                      </select>
                      {item.productId && item.box_quantity && item.box_quantity > 0 && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                          <button type="button" onClick={() => handleSaleTypeChange(item._key, 'unit')}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1.5px solid #3b82f6', background: item.saleType === 'unit' ? '#3b82f6' : 'transparent', color: item.saleType === 'unit' ? '#fff' : '#3b82f6', cursor: 'pointer', fontWeight: 600 }}>
                            Unit
                          </button>
                          <button type="button" onClick={() => handleSaleTypeChange(item._key, 'box')}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1.5px solid #8b5cf6', background: item.saleType === 'box' ? '#8b5cf6' : 'transparent', color: item.saleType === 'box' ? '#fff' : '#8b5cf6', cursor: 'pointer', fontWeight: 600 }}>
                            📦 Box ({item.box_quantity} units)
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={st.td}><input type="text" value={item.description} onChange={e => handleItemChange(item._key, 'description', e.target.value)} style={st.inputSm} placeholder="Or type description..." /></td>
                    <td style={st.td}><input type="number" value={item.quantity} min={1} onChange={e => handleItemChange(item._key, 'quantity', Number(e.target.value))} style={{ ...st.inputSm, width: 60, textAlign: 'center' as const }} /></td>
                    <td style={st.td}><input type="number" value={item.unitPrice} min={0} step={0.01} onChange={e => handleItemChange(item._key, 'unitPrice', Number(e.target.value))} style={{ ...st.inputSm, width: 90, textAlign: 'right' as const }} /></td>
                    <td style={st.td}><input type="number" value={item.discount} min={0} max={100} step={0.5} onChange={e => handleItemChange(item._key, 'discount', Number(e.target.value))} style={{ ...st.inputSm, width: 60, textAlign: 'center' as const }} /></td>
                    <td style={st.td}><input type="number" value={item.vatRate} min={0} step={0.01} onChange={e => handleItemChange(item._key, 'vatRate', Number(e.target.value))} style={{ ...st.inputSm, width: 60, textAlign: 'center' as const }} /></td>
                    <td style={{ ...st.td, textAlign: 'right' as const }}>
                      {item.discount > 0 && <div style={{ fontSize: 11, color: '#ef4444', textDecoration: 'line-through' }}>£{gross.toFixed(2)}</div>}
                      <strong style={{ color: '#10b981' }}>£{lineTotal.toFixed(2)}</strong>
                    </td>
                    <td style={{ ...st.td, textAlign: 'center' as const }}>
                      {items.length > 1 && <button type="button" onClick={() => removeItem(item._key)} style={{ color: '#ef4444', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>✖</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <button type="button" onClick={addItem} style={st.addBtn}>➕ Add Item</button>

        {/* VAT Mode Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '10px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', width: 'fit-content' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>VAT Mode:</span>
          {(['exclusive', 'inclusive'] as const).map(mode => (
            <button key={mode} type="button" onClick={() => setTaxMode(mode)}
              style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: taxMode === mode ? '#3b82f6' : '#e5e7eb', color: taxMode === mode ? '#fff' : '#6b7280' }}>
              {mode === 'exclusive' ? 'VAT Exclusive (add on top)' : 'VAT Inclusive (already in price)'}
            </button>
          ))}
        </div>

        {/* Totals */}
        <div style={st.totals}>
          <div style={st.totalRow}><span>Subtotal:</span><span>£{totals.subtotal.toFixed(2)}</span></div>
          {totals.discountTotal > 0 && <div style={{ ...st.totalRow, color: '#ef4444' }}><span>Discount:</span><span>-£{totals.discountTotal.toFixed(2)}</span></div>}
          <div style={st.totalRow}><span>VAT ({taxMode}):</span><span>£{totals.vatAmount.toFixed(2)}</span></div>
          {appliedCredit > 0 && <div style={{ ...st.totalRow, color: '#059669' }}><span>Credit Applied:</span><span>-£{appliedCredit.toFixed(2)}</span></div>}
          <div style={st.totalFinal}><span>Amount Due:</span><span>£{totals.total.toFixed(2)}</span></div>
        </div>

        {/* Status Lifecycle */}
        <div style={{ marginBottom: 20, padding: '16px 20px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Invoice Status Lifecycle</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['Draft', 'Sent', 'Paid', 'Overdue', 'Cancelled'] as const).map(s => {
              const isCurrent = invoiceStatus === s;
              return (
                <button key={s} type="button" onClick={() => setInvoiceStatus(s)}
                  style={{ padding: '8px 16px', borderRadius: 20, border: `2px solid ${STATUS_COLORS[s]}`, background: isCurrent ? STATUS_COLORS[s] : 'transparent', color: isCurrent ? '#fff' : STATUS_COLORS[s], fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{s}</button>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: '#9ca3af' }}>⚠️ Saving with any status other than Draft will lock this invoice from future edits.</div>
        </div>

        {/* Payment reminder */}
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', fontSize: 13, color: '#065f46' }}>
          <strong>Payment reminder:</strong>{' '}
          {paymentTerms === 'Due on Receipt' ? 'Payment is due immediately upon receipt.' : `Payment due within ${PAYMENT_TERMS[paymentTerms]} days (${paymentTerms}).`}
          {' '}Due date: <strong>{form.dueDate}</strong>.
        </div>

        {/* Notes */}
        <label style={st.label}>Notes (Optional)</label>
        <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          style={{ ...st.input, height: 80, resize: 'vertical' as const }} placeholder="Payment instructions, bank details, thank you message..." />

        {/* Audit Reason */}
        <div style={{ padding: '14px 18px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 10, marginBottom: 20 }}>
          <label style={{ ...st.label, color: '#92400e' }}>⚠️ Reason for Modification (required — audit log)</label>
          <textarea value={editReason} onChange={e => setEditReason(e.target.value)} rows={2}
            style={{ ...st.input, marginBottom: 0, borderColor: editReason ? '#e5e7eb' : '#fbbf24' }}
            placeholder="e.g. Customer requested item update / Corrected tax rate / Added missing service..." required />
        </div>

        {/* Submit Row */}
        <div className="ie-submit-row" style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button type="button" onClick={() => setShowPreview(true)}
            style={{ flex: 1, padding: '12px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15, boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}>
            👁 Preview
          </button>
          <button type="submit" disabled={saving}
            style={{ flex: 2, padding: '13px 20px', background: saving ? '#9ca3af' : '#10b981', color: '#fff', border: 'none', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 15, boxShadow: saving ? 'none' : '0 4px 12px rgba(16,185,129,0.3)' }}>
            {saving ? '⏳ Saving...' : '✅ Save Changes'}
          </button>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div style={st.modalOverlay}>
            <div style={{ ...st.modal, minWidth: 560, maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' as const }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '3px solid #10b981', paddingBottom: 12 }}>
                <div>
                  <h2 style={{ margin: 0, color: '#1f2937' }}>🧾 Invoice Preview</h2>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{form.invoiceNumber}</div>
                </div>
                <button type="button" onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>✕</button>
              </div>
              <div style={{ marginBottom: 16 }}>
                <strong>Bill To:</strong>
                <div>{selectedCustomer?.name || searchTerm}</div>
                {form.email && <div style={{ color: '#6b7280', fontSize: 13 }}>{form.email}</div>}
                {form.companyName && <div style={{ color: '#6b7280', fontSize: 13 }}>{form.companyName}</div>}
                {form.customerAddress && <div style={{ color: '#6b7280', fontSize: 13, whiteSpace: 'pre-line' as const }}>{form.customerAddress}</div>}
              </div>
              <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 13 }}>
                <div><strong>Invoice Date:</strong> {form.invoiceDate}</div>
                <div><strong>Due:</strong> {form.dueDate}</div>
                <div><strong>Status:</strong> {invoiceStatus}</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                <thead><tr style={{ background: '#f3f4f6' }}>
                  <th style={{ padding: 8, textAlign: 'left' as const, border: '1px solid #e5e7eb' }}>Description</th>
                  <th style={{ padding: 8, textAlign: 'center' as const, border: '1px solid #e5e7eb' }}>Qty</th>
                  <th style={{ padding: 8, textAlign: 'right' as const, border: '1px solid #e5e7eb' }}>Unit Price</th>
                  <th style={{ padding: 8, textAlign: 'center' as const, border: '1px solid #e5e7eb' }}>Disc %</th>
                  <th style={{ padding: 8, textAlign: 'center' as const, border: '1px solid #e5e7eb' }}>VAT %</th>
                  <th style={{ padding: 8, textAlign: 'right' as const, border: '1px solid #e5e7eb' }}>Total</th>
                </tr></thead>
                <tbody>
                  {items.filter(i => i.description && i.quantity > 0).map(item => {
                    const net = item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100);
                    return <tr key={item._key}>
                      <td style={{ padding: 8, border: '1px solid #e5e7eb' }}>{item.description}</td>
                      <td style={{ padding: 8, textAlign: 'center' as const, border: '1px solid #e5e7eb' }}>{item.quantity}</td>
                      <td style={{ padding: 8, textAlign: 'right' as const, border: '1px solid #e5e7eb' }}>£{item.unitPrice.toFixed(2)}</td>
                      <td style={{ padding: 8, textAlign: 'center' as const, border: '1px solid #e5e7eb' }}>{item.discount || 0}%</td>
                      <td style={{ padding: 8, textAlign: 'center' as const, border: '1px solid #e5e7eb' }}>{item.vatRate}%</td>
                      <td style={{ padding: 8, textAlign: 'right' as const, border: '1px solid #e5e7eb' }}>£{net.toFixed(2)}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
              <div style={{ maxWidth: 260, marginLeft: 'auto', fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span>Subtotal:</span><span>£{totals.subtotal.toFixed(2)}</span></div>
                {totals.discountTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#ef4444' }}><span>Discount:</span><span>-£{totals.discountTotal.toFixed(2)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span>VAT:</span><span>£{totals.vatAmount.toFixed(2)}</span></div>
                {appliedCredit > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#059669' }}><span>Credit Applied:</span><span>-£{appliedCredit.toFixed(2)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16, paddingTop: 8, borderTop: '2px solid #10b981' }}><span>Amount Due:</span><span>£{totals.total.toFixed(2)}</span></div>
              </div>
              {form.notes && <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#6b7280' }}><strong>Notes:</strong> {form.notes}</div>}
              <button type="button" onClick={() => setShowPreview(false)} style={{ marginTop: 20, width: '100%', padding: 14, background: '#6b7280', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>✏️ Back to Edit</button>
            </div>
          </div>
        )}

        {/* Apply Credit Modal */}
        {showCreditModal && (
          <div style={st.modalOverlay}>
            <div style={st.modal}>
              <h3 style={{ marginTop: 0 }}>💰 Apply Credit</h3>
              <p>Available credit: <strong>£{creditBalance.toFixed(2)}</strong></p>
              <label style={st.label}>Amount to apply (£)</label>
              <input type="number" min={0} max={creditBalance} step={0.01} defaultValue={Math.min(creditBalance, totals.total)} id="editCreditInput" style={st.input} />
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button type="button" style={{ flex: 1, padding: 12, background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => { const v = parseFloat((document.getElementById('editCreditInput') as HTMLInputElement).value) || 0; setAppliedCredit(Math.min(v, creditBalance, totals.total)); setShowCreditModal(false); }}>Apply</button>
                <button type="button" style={{ flex: 1, padding: 12, background: '#6b7280', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowCreditModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

      </form>
    </>
  );
};

const st: any = {
  form: { maxWidth: 920, margin: '30px auto', padding: 40, background: '#fff', borderRadius: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', fontFamily: 'system-ui,-apple-system,sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, paddingBottom: 20, borderBottom: '3px solid #10b981' },
  title: { margin: 0, fontSize: 26, fontWeight: 700, color: '#1f2937' },
  invoiceNumber: { padding: '8px 16px', background: '#3b82f6', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 14 },
  section: { marginBottom: 25, padding: 20, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' },
  sectionTitle: { margin: '0 0 15px', fontSize: 16, fontWeight: 600, color: '#374151' },
  toggleBtn: { padding: '12px 24px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, flex: 1, fontSize: 14 },
  toggleActive: { backgroundColor: '#3b82f6', color: '#fff', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
  toggleInactive: { backgroundColor: '#fff', color: '#6b7280', border: '2px solid #e5e7eb' },
  label: { display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14, color: '#374151' },
  input: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '2px solid #e5e7eb', marginBottom: 15, fontSize: 14, boxSizing: 'border-box' as const },
  dropdown: { position: 'absolute' as const, top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,0.12)', zIndex: 1000, maxHeight: 280, overflowY: 'auto' as const, marginTop: -10 },
  dropdownItem: { padding: '12px 15px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
  selectedBadge: { padding: 12, background: '#d1fae5', borderRadius: 8, border: '2px solid #10b981', marginTop: -10, marginBottom: 15, color: '#059669', fontWeight: 600 },
  creditBanner: { background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#92400e' },
  creditApplyBtn: { background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  creditApplied: { background: '#d1fae5', border: '1px solid #10b981', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14, color: '#065f46' },
  th: { padding: 12, textAlign: 'left' as const, fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, borderBottom: '2px solid #e5e7eb' },
  td: { padding: 12 },
  select: { width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 },
  inputSm: { width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 },
  addBtn: { marginBottom: 25, padding: '10px 20px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', boxShadow: '0 2px 8px rgba(16,185,129,0.3)' },
  totals: { marginTop: 25, padding: 20, borderTop: '2px solid #10b981', borderRadius: 12, background: '#f0fdf4', maxWidth: 350, marginLeft: 'auto', marginBottom: 25 },
  totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 15, color: '#6b7280' },
  totalFinal: { display: 'flex', justifyContent: 'space-between', marginBottom: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' },
  modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { background: '#fff', borderRadius: 12, padding: 30, minWidth: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
};

export default InvoiceEdit;
